/* eslint-disable react-refresh/only-export-components -- buttonVariants is a public styling contract shared by primitives. */
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";
import { Loader2 } from "lucide-react"

import { cn } from "../lib/utils"
import { buttonFocus, controlDisabled } from "../styles/recipes.js"
import { shouldRenderButtonSpinner } from "./button-loading.js"

const buttonVariants = cva(
  `inline-flex items-center justify-center gap-[var(--ds-space-control-gap)] whitespace-nowrap rounded-[var(--ds-radius-control)] text-sm font-medium tracking-[var(--ds-type-control-tracking)] transition-[color,background-color,border-color,box-shadow,opacity,transform] active:scale-[var(--ds-motion-press-scale)] motion-reduce:transition-none motion-reduce:active:scale-100 ${controlDisabled} [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-[var(--ds-icon-size-sm)] shrink-0 [&_svg]:shrink-0 ${buttonFocus} aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/35 aria-invalid:border-destructive`,
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-[var(--shadow-control)] hover:bg-[var(--color-primary-hover)]",
        destructive:
          "bg-destructive text-destructive-foreground shadow-[var(--shadow-control)] hover:bg-destructive/90 focus-visible:ring-destructive/25",
        outline:
          "border border-border bg-card text-foreground shadow-[var(--shadow-control)] hover:border-border-hover hover:bg-accent hover:text-accent-foreground",
        secondary:
          "border border-border bg-[image:var(--button-secondary-gradient)] text-secondary-foreground shadow-[var(--shadow-control)] hover:bg-[image:var(--button-secondary-gradient-hover)]",
        success:
          "bg-success text-success-foreground shadow-[var(--shadow-control)] hover:bg-success/90",
        warning:
          "bg-warning text-warning-foreground shadow-[var(--shadow-control)] hover:bg-warning/90 focus-visible:ring-warning/25",
        ghost:
          "text-foreground hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-[var(--ds-density-control-height)] px-[var(--ds-control-padding-inline-lg)] py-[var(--ds-space-2)] has-[>svg]:px-[var(--ds-control-padding-inline)]",
        sm: "h-[var(--ds-density-control-height-sm)] rounded-[var(--ds-radius-control-compact)] gap-[var(--ds-space-1-5)] px-[var(--ds-control-padding-inline)] has-[>svg]:px-[var(--ds-control-padding-inline-sm)]",
        lg: "h-[var(--ds-density-control-height-lg)] px-[var(--ds-control-padding-inline-lg)] has-[>svg]:px-[var(--ds-control-padding-inline-lg)]",
        icon: "size-[var(--ds-density-control-height)]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  children,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : "button"
  const disabled = Boolean(props.disabled || loading);
  const showSpinner = shouldRenderButtonSpinner(loading, asChild);

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
      aria-busy={loading || undefined}
      disabled={disabled}>
      {showSpinner ? (
        <>
          <Loader2 className="size-[var(--ds-icon-size-sm)] animate-spin motion-reduce:animate-none" aria-hidden />
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
